import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { binencode } from "@/lib/binary";
import { MessagePacketType, WiredMessagePacket } from "@/moby/db";
import { mui } from "@/mui";
import * as Listbox from "@diceui/listbox";
import { bytesToHex } from "@noble/curves/abstract/utils";
import { CheckIcon } from "lucide-react";
import { getPeerAvatarColor, renderPeerAvatarEmoji } from "@/lib/peer-profile-utils";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import {
  createGroupInviteMessage,
  useMobyDB,
  useMobyNodeClient,
  useSession,
} from "../provider";

export function InviteUsersToGroupDialog({
  groupAddress,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  groupAddress: string;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const node = useMobyNodeClient();
  const db = useMobyDB();
  const session = useSession();
  if (session === undefined) {
    throw new Error("Session is undefined");
  }
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedContactAddresses, setSelectedContactAddresses] = useState<
    string[]
  >([]);
  
  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const group = useSuspenseLiveQuery(async () => {
    const group = await db.chatByGroupAddress(groupAddress);
    if (group === undefined) {
      throw new Error("Group not found");
    }
    return group;
  }, [groupAddress]);

  const contacts = useSuspenseLiveQuery(async () => {
    const contacts = await db.contacts.toArray();
    return contacts;
  }, []);

  const onSubmit = async () => {
    const inviteMessagePromises: ReturnType<typeof createGroupInviteMessage>[] =
      [];

    for (const inviteeAddress of selectedContactAddresses) {
      inviteMessagePromises.push(
        createGroupInviteMessage(db, groupAddress, session, inviteeAddress),
      );
    }

    const inviteMessages = await Promise.all(inviteMessagePromises);

    await db.transaction("rw", db.chats, db.messages, async () => {
      await Promise.all(
        inviteMessages.map((inviteMessage) =>
          db.storeMessage(session.address, inviteMessage),
        ),
      );
    });

    for (const inviteMessage of inviteMessages) {
      const peer = node.getOrCreatePeer(
        bytesToHex(inviteMessage.body.to.address),
      );

      if (peer.connection.connectionState !== "connected") {
        continue;
      }

      void peer.chat.send(
        binencode(WiredMessagePacket, {
          type: MessagePacketType.MESSAGE,
          message: inviteMessage,
        }),
      );
    }

    toast.success("Invited users to the group!");

    setOpen(false);
  };

  return (
    <mui.Dialog.Root open={open} onOpenChange={setOpen}>
      {children}
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop onClick={(e) => e.stopPropagation()} />
        <mui.Dialog.Popup onClick={(e) => e.stopPropagation()}>
          <mui.Dialog.Title>Invite Users to Group</mui.Dialog.Title>
          <mui.Dialog.Description>
            Enter the addresses of the people you want to invite to the group.
          </mui.Dialog.Description>

          <Listbox.Root
            className="-mx-6 mt-6 max-h-64 overflow-x-hidden overflow-y-auto"
            multiple
            value={selectedContactAddresses}
            onValueChange={setSelectedContactAddresses}
          >
            {contacts.map((row) => {
              return (
                <Listbox.Item
                  key={row.address}
                  value={row.address}
                  className="border-b-outline group relative block border-b p-4 hover:cursor-pointer data-disabled:pointer-events-none data-disabled:opacity-38"
                  disabled={group.peers.includes(row.address)}
                >
                  <span
                    aria-hidden
                    className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 transition"
                  />

                  <div className="flex gap-4">
                    <div className="relative size-9 shrink-0">
                      <div 
                        className="grid size-9 place-items-center rounded-full text-white"
                        style={{ backgroundColor: getPeerAvatarColor(row.custom?.profile) }}
                      >
                        <div className="text-label-md">{row.getInitials()}</div>
                        {renderPeerAvatarEmoji(row.custom?.profile, "sm")}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-title-md truncate">
                        {row.getDisplayName()}
                      </div>
                      <div className="text-label-sm truncate font-mono">
                        {row.address}
                      </div>
                    </div>
                    <Listbox.ItemIndicator className="self-center">
                      <CheckIcon size={16} />
                    </Listbox.ItemIndicator>
                  </div>
                </Listbox.Item>
              );
            })}
          </Listbox.Root>

          <div className="mt-4">
            <mui.Button
              variant="primary"
              filled
              className="w-full"
              disabled={selectedContactAddresses.length === 0}
              onClick={onSubmit}
            >
              Invite
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
  );
}
